import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Typography, Card, Row, Col, Divider, Dropdown, Space, Modal } from "antd";
import {
  RocketOutlined,
  ApiOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  DashboardOutlined,
  SyncOutlined,
  BarChartOutlined,
  BankOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  MenuOutlined,
  CloseOutlined,
  DollarOutlined,
  LineChartOutlined,
  LoginOutlined,
  DownOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import { useTheme } from "../store/context/theme-context";
import { useTenant } from "../store/context/tenant-context";
import { useFeatures } from "../store/context/feature-context";
import { featuresEnum } from "../shared/constants/constants";
import LanguageSelector from "../shared/languages/LanguageSelector";
import TenantSwitcher from "../components/General/TenantSwitcher";
import { useTranslation } from "react-i18next";
import productsData from "../utils/data/products";

// Import loan images
import PayDayImage from "../assets/payday-loan.jpg";
import PayrollImage from "../assets/payroll-loan.jpg";
import SMEImage from "../assets/SME-loan.jpg";
import AssetImage from "../assets/asset-financing-loan.jpg";

const { Title, Text, Paragraph } = Typography;

const LandingPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const tenant = useTenant();
  const { findFeature } = useFeatures();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loanModalVisible, setLoanModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productModalVisible, setProductModalVisible] = useState(false);

  // Get loan products from products.js
  const loanProducts = productsData.filter((product) => product.key === "loans");

  // Tenant type classification
  // ONLY BankLingo tenant should see docs - check actual tenant key/domain
  const isBankLingo = tenant.companyName?.toLowerCase().includes("banklingo");
  
  // Platform clients (StandardLink, RTrust) - they USE BankLingo as a product
  const isPlatformClient = tenant.cssName === "standardlink" || 
                           tenant.cssName === "retrust";
  
  // White-label internet banking (Empire Trust, City Gate) - customers don't see BankLingo
  const isWhiteLabelBank = tenant.cssName === "empiretrust" || 
                           tenant.cssName === "citygate" ||
                           tenant.cssName === "onlineloan";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Platform products (for BankLingo and Platform Clients)
  const platformProducts = [
    {
      id: "core_banking",
      icon: <BankOutlined style={{ fontSize: "32px" }} />,
      title: "Core Banking System",
      description:
        "Complete core banking solution for managing loans and deposit products with optional wallet functionality. Handle accounts, transactions, and customer relationships with enterprise-grade reliability.",
      color: "#1890ff",
      features: [],
    },
    {
      id: "reconciliation",
      icon: <SyncOutlined style={{ fontSize: "32px" }} />,
      title: "Reconciliation Module",
      description:
        "Automated financial reconciliation across all channels. Match transactions, identify discrepancies, and generate reports with complex user-defined matching algorithms that reduce manual effort by 90%.",
      color: "#52c41a",
      features: [featuresEnum.ADMIN_MODULE_ACCOUNTING],
    },
    {
      id: "transaction_monitoring",
      icon: <DashboardOutlined style={{ fontSize: "32px" }} />,
      title: "Transaction Monitoring Module",
      description:
        "Real-time visibility into all banking operations. Track transactions, monitor system health, detect anomalies, and ensure regulatory compliance with comprehensive dashboards and intelligent alerting systems.",
      color: "#722ed1",
      features: [
        featuresEnum.ADMIN_TRANSACTIONS_REPORT,
        featuresEnum.CUSTOMER_TRANSACTIONS,
      ],
    },
    {
      id: "integration",
      icon: <ApiOutlined style={{ fontSize: "32px" }} />,
      title: "Integration Engine",
      description:
        "Seamlessly connect with any system. Pre-built connectors for payment gateways, core banking systems, mobile apps, and third-party services. API-first architecture for rapid integration and digital transformation.",
      color: "#faad14",
      features: [featuresEnum.ADMIN_MANAGE_CHANNELS],
    },
    {
      id: "workflow",
      icon: <ThunderboltOutlined style={{ fontSize: "32px" }} />,
      title: "Workflow Processing Engine",
      description:
        "High-performance transaction processing at scale. BPMN 2.0 workflows automate complex business processes with sub-second response times. Customize approval chains, validation rules, and business logic without code.",
      color: "#fa8c16",
      features: [
        featuresEnum.ADMIN_LOAN_REQUESTS,
        featuresEnum.ADMIN_INSURANCE_REQUESTS,
        featuresEnum.ADMIN_TRANSFER_REQUESTS,
      ],
    },
    {
      id: "loan_origination",
      icon: <DollarOutlined style={{ fontSize: "32px" }} />,
      title: "Loan Origination Platform",
      description:
        "End-to-end loan processing from application to disbursement. Automated credit scoring, document verification, approval workflows, and compliance checks. Reduce loan processing time by 70% with intelligent automation.",
      color: "#eb2f96",
      features: [featuresEnum.CUSTOMER_LOAN, featuresEnum.ADMIN_LOAN_REQUESTS],
    },
  ];

  // Banking products (for White-Label Banks like Empire Trust, City Gate)
  const bankingProducts = [
    {
      id: "accounts",
      icon: <BankOutlined style={{ fontSize: "32px" }} />,
      title: "Savings & Current Accounts",
      description:
        "Open and manage your bank accounts with ease. Enjoy competitive interest rates on savings, zero balance current accounts, and 24/7 access to your funds through our digital banking platform.",
      color: "#1890ff",
      features: [featuresEnum.CUSTOMER_ACCOUNT],
    },
    {
      id: "transfers",
      icon: <DollarOutlined style={{ fontSize: "32px" }} />,
      title: "Money Transfers",
      description:
        "Send money instantly to any bank account. Make local and international transfers, schedule recurring payments, and track all your transactions in real-time with instant confirmations.",
      color: "#52c41a",
      features: [featuresEnum.CUSTOMER_TRANSFER],
    },
    {
      id: "loans",
      icon: <LineChartOutlined style={{ fontSize: "32px" }} />,
      title: "Personal & Business Loans",
      description:
        "Access flexible loan products tailored to your needs. From payday loans to asset financing, we offer competitive rates, quick approval, and convenient repayment options.",
      color: "#722ed1",
      features: [featuresEnum.CUSTOMER_LOAN],
    },
    {
      id: "bills",
      icon: <ThunderboltOutlined style={{ fontSize: "32px" }} />,
      title: "Bill Payments",
      description:
        "Pay your bills conveniently from anywhere. Electricity, water, internet, cable TV, and more. Set up automatic payments and never miss a due date with our smart bill payment system.",
      color: "#faad14",
      features: [featuresEnum.CUSTOMER_BILL],
    },
    {
      id: "airtime_data",
      icon: <ApiOutlined style={{ fontSize: "32px" }} />,
      title: "Airtime & Data",
      description:
        "Recharge airtime and purchase data bundles instantly. Support for all major networks with instant delivery and special discounts for frequent users.",
      color: "#fa8c16",
      features: [
        featuresEnum.CUSTOMER_AIRTIME_RECHARGE,
        featuresEnum.CUSTOMER_DATA_PURCHASE,
      ],
    },
    {
      id: "savings_investment",
      icon: <SafetyOutlined style={{ fontSize: "32px" }} />,
      title: "Savings & Investments",
      description:
        "Grow your wealth with our investment products. Fixed deposits, target savings, and investment schemes with attractive returns. Track your financial goals and watch your money grow.",
      color: "#eb2f96",
      features: [
        featuresEnum.CUSTOMER_SAVINGS,
        featuresEnum.CUSTOMER_INVESTMENT,
      ],
    },
    {
      id: "virtual_accounts",
      icon: <CreditCardOutlined style={{ fontSize: "32px" }} />,
      title: "Virtual Accounts",
      description:
        "Create and manage multiple virtual accounts for different purposes. Segregate funds, automate collections, and streamline your financial operations with dedicated account numbers for each business need.",
      color: "#13c2c2",
      features: [],
    },
  ];

  // Select products based on tenant type
  const allProducts = (isBankLingo || isPlatformClient) ? platformProducts : bankingProducts;

  // Filter products based on tenant's enabled features
  const enabledProducts = allProducts.filter((product) => {
    // For BankLingo and Platform Clients, show all platform products (marketing page)
    if (isBankLingo || isPlatformClient) {
      return true;
    }
    // For white-label banks, filter by enabled features
    if (!product.features || product.features.length === 0) {
      return true;
    }
    // Show if at least one related feature is enabled
    return product.features.some((feature) => findFeature(feature));
  });

  // If no products are enabled, show all (for marketing purposes)
  const products = enabledProducts.length > 0 ? enabledProducts : allProducts;

  // Tenant-specific stats
  const stats = (isBankLingo || isPlatformClient)
    ? [
        { value: "99.99%", label: "Uptime SLA" },
        { value: "<200ms", label: "API Response Time" },
        { value: "24/7", label: "Support Available" },
        { value: `${products.length}+`, label: "Platform Modules" },
      ]
    : [
        // Internet banking stats for Empire Trust, City Gate
        { value: "24/7", label: "Online Banking" },
        { value: "Instant", label: "Transfers" },
        { value: "Secure", label: "Transactions" },
        { value: "Easy", label: "Account Access" },
      ];

  // Tenant-specific features
  const features = (isBankLingo || isPlatformClient)
    ? [
        // Platform features for BankLingo, StandardLink, RTrust
        { icon: <SafetyOutlined />, text: "Enterprise-grade Security" },
        { icon: <ThunderboltOutlined />, text: "Real-time Processing" },
        { icon: <ApiOutlined />, text: "RESTful APIs" },
        { icon: <DashboardOutlined />, text: "Comprehensive Dashboards" },
        { icon: <SyncOutlined />, text: "Multi-channel Integration" },
        { icon: <CheckCircleOutlined />, text: "Compliance Ready" },
      ]
    : [
        // Internet banking features for Empire Trust, City Gate
        { icon: <DollarOutlined />, text: "Easy Transfers" },
        { icon: <BankOutlined />, text: "Bill Payments" },
        { icon: <LineChartOutlined />, text: "Account Management" },
        { icon: <SafetyOutlined />, text: "Secure Banking" },
        { icon: <ThunderboltOutlined />, text: "Instant Transactions" },
        { icon: <CheckCircleOutlined />, text: "24/7 Access" },
      ];

  const isDark = theme === "dark";

  return (
    <div
      style={{
        backgroundColor: isDark ? "#141414" : "#ffffff",
        minHeight: "100vh",
        color: isDark ? "#ffffff" : "#000000",
      }}
    >
      {/* Navigation Bar */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: scrolled
            ? isDark
              ? "rgba(20, 20, 20, 0.95)"
              : "rgba(255, 255, 255, 0.95)"
            : isDark
              ? `${tenant.theme.colorPrimary}40`
              : `${tenant.theme.colorPrimary}30`,
          backdropFilter: scrolled ? "blur(10px)" : "blur(8px)",
          borderBottom: scrolled 
            ? `1px solid ${isDark ? "#303030" : "#f0f0f0"}` 
            : `1px solid ${tenant.theme.colorPrimary}50`,
          transition: "all 0.3s ease",
          padding: "16px 0",
        }}
      >
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "0 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <img
              src={tenant.logo}
              alt={tenant.name}
              style={{ height: "40px", objectFit: "contain" }}
            />
            <Text
              strong
              style={{
                fontSize: "20px",
                color: isDark ? "#ffffff" : "#000000",
              }}
            >
              {tenant.name}
            </Text>
            <span
              style={{
                fontSize: "13px",
                color: isDark ? "#bbb" : "#666",
                marginLeft: "8px",
                fontWeight: "600",
                opacity: 0.9,
              }}
            >
              {(isBankLingo || isPlatformClient)
                ? "Powered by BankLingo"
                : `Powered by ${tenant.companyName}`}
            </span>
          </div>

          {/* Desktop Menu */}
          <div
            style={{
              display: "flex",
              gap: "24px",
              alignItems: "center",
            }}
            className="hidden md:flex"
          >
            {/* Development Tenant Switcher - Only show on localhost */}
            {process.env.NODE_ENV === 'development' && 
             (window.location.hostname === 'localhost' || 
              window.location.hostname === '127.0.0.1') && (
              <TenantSwitcher />
            )}

            <a
              href="#products"
              style={{
                color: isDark ? "#ffffff" : "#000000",
                textDecoration: "none",
              }}
            >
              Products
            </a>
            {findFeature(featuresEnum.CUSTOMER_LOAN) && (
              <a
                href="#loans"
                style={{
                  color: isDark ? "#ffffff" : "#000000",
                  textDecoration: "none",
                }}
              >
                Loans
              </a>
            )}
            <a
              href="#features"
              style={{
                color: isDark ? "#ffffff" : "#000000",
                textDecoration: "none",
              }}
            >
              Features
            </a>
            {isBankLingo && (
              <a
                onClick={() => navigate("/company-profile")}
                style={{
                  color: isDark ? "#ffffff" : "#000000",
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                Company
              </a>
            )}
            {isBankLingo && (
              <a
                href="https://documentation.banklingo.app"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: isDark ? "#ffffff" : "#000000",
                  textDecoration: "none",
                }}
              >
                Documentation
              </a>
            )}
            
            {/* Account Access Dropdown */}
            <Dropdown
              menu={{
                items: [
                  // Customer Login - BankLingo and White-Label Banks only, not Platform Clients
                  ...((!isPlatformClient) ? [{
                    key: "customer",
                    icon: <UserOutlined style={{ fontSize: "16px" }} />,
                    label: (
                      <div style={{ padding: "8px 4px" }}>
                        <div style={{ fontWeight: "600", fontSize: "15px", marginBottom: "4px" }}>
                          Customer Login
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: isDark ? "#999" : "#666",
                            lineHeight: "1.4",
                          }}
                        >
                          Access your personal account
                        </div>
                      </div>
                    ),
                    onClick: () => navigate("/authentication/login"),
                  }] : []),
                  // Admin Login - All tenant types
                  {
                    key: "admin",
                    icon: <BankOutlined style={{ fontSize: "16px" }} />,
                    label: (
                      <div style={{ padding: "8px 4px" }}>
                        <div style={{ fontWeight: "600", fontSize: "15px", marginBottom: "4px" }}>
                          Admin Login
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: isDark ? "#999" : "#666",
                            lineHeight: "1.4",
                          }}
                        >
                          Access admin portal
                        </div>
                      </div>
                    ),
                    onClick: () => navigate("/authentication/admin/login"),
                  },
                  // Divider and Create Account - BankLingo and White-Label Banks only, not Platform Clients
                  ...((!isPlatformClient) ? [
                    {
                      type: "divider",
                    },
                    {
                      key: "signup",
                      icon: <RocketOutlined style={{ fontSize: "16px", color: tenant.theme.colorPrimary }} />,
                      label: (
                        <div style={{ padding: "8px 4px" }}>
                          <div style={{ fontWeight: "600", fontSize: "15px", marginBottom: "4px", color: tenant.theme.colorPrimary }}>
                            Create Account
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              color: isDark ? "#999" : "#666",
                              lineHeight: "1.4",
                            }}
                          >
                            Get started with {tenant.name}
                          </div>
                        </div>
                      ),
                      onClick: () => navigate("/authentication?mode=signup"),
                    }
                  ] : []),
                ],
                style: {
                  padding: "8px",
                  minWidth: "280px",
                  borderRadius: "12px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                },
              }}
              trigger={["click"]}
            >
              <Button
                type="primary"
                style={{
                  backgroundColor: tenant.theme.colorPrimary,
                  borderColor: tenant.theme.colorPrimary,
                }}
              >
                <Space>
                  <UserOutlined />
                  Account Access
                  <DownOutlined style={{ fontSize: "10px" }} />
                </Space>
              </Button>
            </Dropdown>
            
            <LanguageSelector />
          </div>

          {/* Mobile Menu Toggle */}
          <Button
            type="text"
            icon={mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden"
            style={{ color: isDark ? "#ffffff" : "#000000" }}
          />
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            style={{
              backgroundColor: isDark ? "#1f1f1f" : "#ffffff",
              padding: "24px",
              borderTop: `1px solid ${isDark ? "#303030" : "#f0f0f0"}`,
            }}
            className="md:hidden"
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <a
                href="#products"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  color: isDark ? "#ffffff" : "#000000",
                  textDecoration: "none",
                }}
              >
                Products
              </a>
              {findFeature(featuresEnum.CUSTOMER_LOAN) && (
                <a
                  href="#loans"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    color: isDark ? "#ffffff" : "#000000",
                    textDecoration: "none",
                  }}
                >
                  Loans
                </a>
              )}
              <a
                href="#features"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  color: isDark ? "#ffffff" : "#000000",
                  textDecoration: "none",
                }}
              >
                Features
              </a>
              {isBankLingo && (
                <a
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate("/company-profile");
                  }}
                  style={{
                    color: isDark ? "#ffffff" : "#000000",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                >
                  Company
                </a>
              )}
              {isBankLingo && (
                <a
                  href="https://documentation.banklingo.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: isDark ? "#ffffff" : "#000000",
                    textDecoration: "none",
                  }}
                >
                  Documentation
                </a>
              )}
              <Divider style={{ margin: "8px 0" }} />
              
              {/* Account Access Section */}
              <div
                style={{
                  backgroundColor: isDark ? "#141414" : "#f5f5f5",
                  padding: "16px",
                  borderRadius: "8px",
                }}
              >
                <Text
                  style={{
                    color: isDark ? "#8c8c8c" : "#595959",
                    fontSize: "12px",
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Account Access
                </Text>
                
                <div
                  style={{
                    marginTop: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {/* Customer Login - BankLingo and White-Label Banks only, not Platform Clients */}
                  {!isPlatformClient && (
                    <Button
                      type="default"
                      block
                      onClick={() => {
                        navigate("/authentication/login");
                        setMobileMenuOpen(false);
                      }}
                      style={{
                        textAlign: "left",
                        height: "auto",
                        padding: "12px",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                          <UserOutlined /> Customer Login
                        </div>
                        <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                          Access your personal account
                        </div>
                      </div>
                    </Button>
                  )}
                  
                  {/* Admin Login - All tenant types */}
                  <Button
                    type="default"
                    block
                    onClick={() => {
                      navigate("/authentication/admin/login");
                      setMobileMenuOpen(false);
                    }}
                    style={{
                      textAlign: "left",
                      height: "auto",
                      padding: "12px",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                        <BankOutlined /> Admin Login
                      </div>
                      <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                        Access admin portal
                      </div>
                    </div>
                  </Button>
                  
                  {/* Create Account - BankLingo and White-Label Banks only, not Platform Clients */}
                  {!isPlatformClient && (
                    <Button
                      type="primary"
                      block
                      onClick={() => {
                        navigate("/authentication?mode=signup");
                        setMobileMenuOpen(false);
                      }}
                      style={{
                        backgroundColor: tenant.theme.colorPrimary,
                        borderColor: tenant.theme.colorPrimary,
                        textAlign: "left",
                        height: "auto",
                        padding: "12px",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                          <RocketOutlined /> Create Account
                        </div>
                        <div style={{ fontSize: "12px", opacity: 0.9 }}>
                          Get started with {tenant.name}
                        </div>
                      </div>
                    </Button>
                  )}
                </div>
              </div>
              
              <Divider style={{ margin: "8px 0" }} />
              <LanguageSelector />
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section
        style={{
          paddingTop: "120px",
          paddingBottom: "80px",
          background: isDark
            ? `linear-gradient(135deg, #1a1a2e 0%, ${tenant.theme.colorPrimary}40 50%, #16213e 100%)`
            : `linear-gradient(135deg, ${tenant.theme.colorPrimary}85 0%, ${tenant.theme.colorPrimary}95 100%)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated Background Elements */}
        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translateY(0px) translateX(0px); }
              33% { transform: translateY(-30px) translateX(20px); }
              66% { transform: translateY(-15px) translateX(-20px); }
            }
            
            @keyframes pulse {
              0%, 100% { opacity: 0.3; transform: scale(1); }
              50% { opacity: 0.6; transform: scale(1.1); }
            }
            
            @keyframes drift {
              0% { transform: translateX(0px) translateY(0px); }
              25% { transform: translateX(30px) translateY(-30px); }
              50% { transform: translateX(60px) translateY(0px); }
              75% { transform: translateX(30px) translateY(30px); }
              100% { transform: translateX(0px) translateY(0px); }
            }
            
            @keyframes gradientShift {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
            
            .animated-bg-overlay {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: linear-gradient(
                45deg,
                rgba(255, 255, 255, 0.05) 0%,
                rgba(255, 255, 255, 0) 50%,
                rgba(255, 255, 255, 0.05) 100%
              );
              background-size: 400% 400%;
              animation: gradientShift 15s ease infinite;
            }
            
            .floating-shape {
              position: absolute;
              border-radius: 50%;
              filter: blur(60px);
              opacity: 0.4;
            }
            
            .shape-1 {
              width: 300px;
              height: 300px;
              background: rgba(255, 255, 255, 0.15);
              top: 10%;
              right: 5%;
              animation: float 20s ease-in-out infinite;
            }
            
            .shape-2 {
              width: 400px;
              height: 400px;
              background: rgba(255, 255, 255, 0.1);
              bottom: 10%;
              left: 5%;
              animation: float 25s ease-in-out infinite reverse;
            }
            
            .shape-3 {
              width: 250px;
              height: 250px;
              background: rgba(255, 255, 255, 0.12);
              top: 50%;
              left: 50%;
              animation: pulse 18s ease-in-out infinite;
            }
            
            .floating-particle {
              position: absolute;
              width: 4px;
              height: 4px;
              background: rgba(255, 255, 255, 0.6);
              border-radius: 50%;
              box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
            }
          `}
        </style>
        
        {/* Animated overlay */}
        <div className="animated-bg-overlay" />
        
        {/* Floating shapes */}
        <div className="floating-shape shape-1" />
        <div className="floating-shape shape-2" />
        <div className="floating-shape shape-3" />
        
        {/* Floating particles */}
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="floating-particle"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `drift ${15 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
              opacity: 0.3 + Math.random() * 0.3,
            }}
          />
        ))}
        
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "0 24px",
            textAlign: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Title
            level={1}
            style={{
              color: "#ffffff",
              fontSize: "48px",
              marginBottom: "24px",
              fontWeight: "700",
            }}
          >
            {(isBankLingo || isPlatformClient)
              ? "BankLingo Digitalization Platform"
              : `Welcome to ${tenant.companyName}`}
          </Title>
          <Paragraph
            style={{
              color: "#ffffff",
              fontSize: "20px",
              maxWidth: "700px",
              margin: "0 auto 40px",
              opacity: 0.9,
            }}
          >
            {(isBankLingo || isPlatformClient)
              ? "Transform your financial institution with a complete digital banking ecosystem. Powerful APIs, real-time processing, and enterprise-grade security built for scale."
              : `${tenant.tagLine || "Your trusted partner for secure and convenient banking."} Access your accounts, make transfers, pay bills, and manage your finances 24/7 from anywhere.`}
          </Paragraph>

          <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap" }}>
            {/* Create Account/Get Started - Only for BankLingo and White-Label Banks */}
            {!isPlatformClient && (
              <Button
                type="primary"
                size="large"
                icon={<RocketOutlined />}
                onClick={() => navigate("/authentication?mode=signup")}
                style={{
                  height: "48px",
                  fontSize: "16px",
                  backgroundColor: "#ffffff",
                  color: tenant.theme.colorPrimary,
                  borderColor: "#ffffff",
                }}
              >
                {(isBankLingo || isPlatformClient) ? "Get Started" : "Open Account"}
              </Button>
            )}
            {/* API Documentation - Only for BankLingo */}
            {isBankLingo && (
              <Button
                size="large"
                icon={<ApiOutlined />}
                onClick={() =>
                  window.open("https://documentation.banklingo.app/docs/api", "_blank")
                }
                style={{
                  height: "48px",
                  fontSize: "16px",
                  backgroundColor: "transparent",
                  color: "#ffffff",
                  borderColor: "#ffffff",
                }}
              >
                API Documentation
              </Button>
            )}
            {/* Admin Login - For Platform Clients only */}
            {isPlatformClient && (
              <Button
                type="primary"
                size="large"
                icon={<BankOutlined />}
                onClick={() => navigate("/authentication/admin/login")}
                style={{
                  height: "48px",
                  fontSize: "16px",
                  backgroundColor: "#ffffff",
                  color: tenant.theme.colorPrimary,
                  borderColor: "#ffffff",
                }}
              >
                Admin Login
              </Button>
            )}
          </div>

          {/* Stats */}
          <Row
            gutter={[24, 24]}
            style={{ marginTop: "60px" }}
            justify="center"
          >
            {stats.map((stat, index) => (
              <Col xs={12} sm={6} key={index}>
                <div style={{ textAlign: "center" }}>
                  <Title
                    level={2}
                    style={{
                      color: "#ffffff",
                      marginBottom: "8px",
                      fontSize: "32px",
                    }}
                  >
                    {stat.value}
                  </Title>
                  <Text style={{ color: "#ffffff", opacity: 0.8 }}>
                    {stat.label}
                  </Text>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Products Section */}
      <section
        id="products"
        style={{
          padding: "80px 24px",
          backgroundColor: isDark ? "#000000" : "#fafafa",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <Title
              level={2}
              style={{
                color: isDark ? "#ffffff" : "#000000",
                fontSize: "40px",
                marginBottom: "16px",
              }}
            >
              {(isBankLingo || isPlatformClient) ? "Complete Banking Suite" : "Our Services"}
            </Title>
            <Paragraph
              style={{
                color: isDark ? "#999999" : "#666666",
                fontSize: "18px",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              {(isBankLingo || isPlatformClient)
                ? "Everything you need to run a modern financial institution"
                : "Comprehensive banking services designed for your needs"}
            </Paragraph>
          </div>

          <Row gutter={[32, 32]}>
            {products.map((product, index) => (
              <Col xs={24} md={12} lg={8} key={index}>
                <Card
                  hoverable
                  style={{
                    height: "100%",
                    backgroundColor: isDark ? "#1f1f1f" : "#ffffff",
                    border: `1px solid ${isDark ? "#303030" : "#f0f0f0"}`,
                    borderRadius: "12px",
                    transition: "all 0.3s ease",
                  }}
                  bodyStyle={{ height: "100%", display: "flex", flexDirection: "column" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-8px)";
                    e.currentTarget.style.boxShadow = isDark
                      ? "0 12px 24px rgba(0, 0, 0, 0.5)"
                      : "0 12px 24px rgba(0, 0, 0, 0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "12px",
                      backgroundColor: `${product.color}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "20px",
                      color: product.color,
                    }}
                  >
                    {product.icon}
                  </div>
                  <Title
                    level={4}
                    style={{
                      color: isDark ? "#ffffff" : "#000000",
                      marginBottom: "12px",
                    }}
                  >
                    {product.title}
                  </Title>
                  <Paragraph
                    style={{
                      color: isDark ? "#999999" : "#666666",
                      flex: 1,
                    }}
                  >
                    {product.description}
                  </Paragraph>
                  <Button
                    type="link"
                    icon={<ArrowRightOutlined />}
                    style={{ padding: 0, color: product.color }}
                    onClick={() => {
                      setSelectedProduct(product);
                      setProductModalVisible(true);
                    }}
                  >
                    Learn More
                  </Button>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        style={{
          padding: "80px 24px",
          backgroundColor: isDark ? "#141414" : "#ffffff",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <Title
              level={2}
              style={{
                color: isDark ? "#ffffff" : "#000000",
                fontSize: "40px",
                marginBottom: "16px",
              }}
            >
              {(isBankLingo || isPlatformClient) ? "Built for Enterprise" : "Banking Made Easy"}
            </Title>
            <Paragraph
              style={{
                color: isDark ? "#999999" : "#666666",
                fontSize: "18px",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              {(isBankLingo || isPlatformClient)
                ? "Enterprise-grade features that scale with your business"
                : "Everything you need for convenient and secure online banking"}
            </Paragraph>
          </div>

          <Row gutter={[24, 24]}>
            {features.map((feature, index) => (
              <Col xs={24} sm={12} md={8} key={index}>
                <div
                  style={{
                    textAlign: "center",
                    padding: "24px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "40px",
                      color: tenant.theme.colorPrimary,
                      marginBottom: "16px",
                    }}
                  >
                    {feature.icon}
                  </div>
                  <Text
                    style={{
                      fontSize: "16px",
                      color: isDark ? "#ffffff" : "#000000",
                    }}
                  >
                    {feature.text}
                  </Text>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Loans Section - Only show for white-label banks (Empire Trust, City Gate), not BankLingo or platform clients */}
      {isWhiteLabelBank && findFeature(featuresEnum.CUSTOMER_LOAN) && (
        <section
          id="loans"
          style={{
            padding: "80px 24px",
            backgroundColor: isDark ? "#1a1a1a" : "#fafafa",
          }}
        >
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            {/* Section Header */}
            <div style={{ textAlign: "center", marginBottom: "48px" }}>
              <Title
                level={2}
                style={{
                  color: isDark ? "#ffffff" : "#000000",
                  fontSize: "36px",
                  marginBottom: "16px",
                }}
              >
                Apply for Loans
              </Title>
              <Paragraph
                style={{
                  fontSize: "18px",
                  color: isDark ? "#999999" : "#666666",
                  maxWidth: "700px",
                  margin: "0 auto",
                }}
              >
                We offer affordable payroll loan, payday loan, SMEs loan, and asset
                finance loans that protect you and your loved ones against financial
                loss.
              </Paragraph>
            </div>

            {/* Loan Cards Grid */}
            <Row gutter={[24, 24]}>
              {loanProducts.map((loan, index) => (
                <Col xs={24} sm={12} lg={6} key={index}>
                  <Card
                    hoverable
                    style={{
                      height: "100%",
                      borderRadius: "12px",
                      overflow: "hidden",
                      border: `1px solid ${
                        isDark ? "#303030" : "#e8e8e8"
                      }`,
                      backgroundColor: isDark ? "#1f1f1f" : "#ffffff",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-8px)";
                      e.currentTarget.style.boxShadow = `0 12px 24px ${tenant.theme.colorPrimary}30`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                    cover={
                      <img
                        alt={loan.title}
                        src={loan.image}
                        style={{
                          height: "200px",
                          objectFit: "cover",
                        }}
                      />
                    }
                  >
                    <Title
                      level={4}
                      style={{
                        color: isDark ? "#ffffff" : "#000000",
                        marginBottom: "12px",
                        fontSize: "18px",
                      }}
                    >
                      {loan.title}
                    </Title>
                    <Paragraph
                      style={{
                        color: isDark ? "#999999" : "#666666",
                        fontSize: "14px",
                        marginBottom: "16px",
                        minHeight: "60px",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {loan.description}
                    </Paragraph>
                    <div style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
                      <Button
                        type="primary"
                        block
                        onClick={() => navigate(loan.url)}
                        style={{
                          backgroundColor: tenant.theme.colorPrimary,
                          borderColor: tenant.theme.colorPrimary,
                          height: "40px",
                          fontWeight: "600",
                        }}
                      >
                        Get Loan
                      </Button>
                      <Button
                        block
                        onClick={() => {
                          setSelectedLoan(loan);
                          setLoanModalVisible(true);
                        }}
                        style={{
                          height: "40px",
                          color: tenant.theme.colorPrimary,
                          borderColor: tenant.theme.colorPrimary,
                        }}
                      >
                        Learn More
                      </Button>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section
        style={{
          padding: "80px 24px",
          background: isDark
            ? `linear-gradient(135deg, ${tenant.theme.colorPrimary}15 0%, ${tenant.theme.colorPrimary}25 100%)`
            : `linear-gradient(135deg, ${tenant.theme.colorPrimary}15 0%, ${tenant.theme.colorPrimary}25 100%)`,
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <Title
            level={2}
            style={{
              color: isDark ? "#ffffff" : "#000000",
              fontSize: "36px",
              marginBottom: "24px",
            }}
          >
            {(isBankLingo || isPlatformClient)
              ? "Ready to Transform Your Banking Operations?"
              : "Ready to Experience Modern Banking?"}
          </Title>
          <Paragraph
            style={{
              color: isDark ? "#999999" : "#666666",
              fontSize: "18px",
              marginBottom: "40px",
            }}
          >
            {(isBankLingo || isPlatformClient)
              ? `Join leading financial institutions already using ${tenant.name} powered by BankLingo`
              : `Open your account today and enjoy seamless, secure banking with ${tenant.companyName}. Your financial journey starts here.`}
          </Paragraph>
          <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap" }}>
            {/* Create Account/Get Started - Only for BankLingo and White-Label Banks */}
            {!isPlatformClient && (
              <Button
                type="primary"
                size="large"
                icon={<RocketOutlined />}
                onClick={() => navigate("/authentication?mode=signup")}
                style={{
                  height: "48px",
                  fontSize: "16px",
                  backgroundColor: tenant.theme.colorPrimary,
                }}
              >
                {(isBankLingo || isPlatformClient) ? "Get Started Today" : "Open Account"}
              </Button>
            )}
            {/* Admin Login - For Platform Clients, or general Sign In for others */}
            <Button
              size="large"
              onClick={() => navigate(isPlatformClient ? "/authentication/admin/login" : "/authentication?mode=login")}
              style={{
                height: "48px",
                fontSize: "16px",
              }}
            >
              {isPlatformClient ? "Admin Login" : "Sign In"}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{
          padding: "40px 24px",
          backgroundColor: isDark ? "#000000" : "#fafafa",
          borderTop: `1px solid ${isDark ? "#303030" : "#f0f0f0"}`,
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Row gutter={[32, 32]}>
            <Col xs={24} md={12}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                <img
                  src={tenant.logo}
                  alt={tenant.name}
                  style={{ height: "32px", objectFit: "contain" }}
                />
                <Text
                  strong
                  style={{
                    fontSize: "16px",
                    color: isDark ? "#ffffff" : "#000000",
                  }}
                >
                  {tenant.name}
                </Text>
              </div>
              <Paragraph style={{ color: isDark ? "#999999" : "#666666" }}>
                {(isBankLingo || isPlatformClient)
                  ? "Powered by BankLingo - Digitalization Platform"
                  : `Powered by ${tenant.companyName} - Secure & Reliable Banking`}
              </Paragraph>
              <Paragraph style={{ color: isDark ? "#999999" : "#666666", fontSize: "12px" }}>
                © {new Date().getFullYear()} {(isBankLingo || isPlatformClient) ? "BankLingo" : tenant.companyName}. All rights reserved.
              </Paragraph>
            </Col>
            <Col xs={24} md={12}>
              <Row gutter={[16, 16]}>
                {/* Only show Resources and Company links for BankLingo */}
                {isBankLingo && (
                  <>
                    <Col xs={12}>
                      <Title
                        level={5}
                        style={{
                          color: isDark ? "#ffffff" : "#000000",
                          marginBottom: "16px",
                        }}
                      >
                        Resources
                      </Title>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <a
                          href="https://documentation.banklingo.app/docs/intro"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: isDark ? "#999999" : "#666666",
                            textDecoration: "none",
                          }}
                        >
                          Getting Started
                        </a>
                        <a
                          href="https://documentation.banklingo.app/docs/api"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: isDark ? "#999999" : "#666666",
                            textDecoration: "none",
                          }}
                        >
                          API Reference
                        </a>
                        <a
                          href="https://documentation.banklingo.app/blog"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: isDark ? "#999999" : "#666666",
                            textDecoration: "none",
                          }}
                        >
                          Release Notes
                        </a>
                      </div>
                    </Col>
                    <Col xs={12}>
                      <Title
                        level={5}
                        style={{
                          color: isDark ? "#ffffff" : "#000000",
                          marginBottom: "16px",
                        }}
                      >
                        Company
                      </Title>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <a
                          href="mailto:support@banklingo.com"
                          style={{
                            color: isDark ? "#999999" : "#666666",
                            textDecoration: "none",
                          }}
                        >
                          Support
                        </a>
                        <a
                          onClick={() => navigate("/contact")}
                          style={{
                            color: isDark ? "#999999" : "#666666",
                            textDecoration: "none",
                            cursor: "pointer",
                          }}
                        >
                          Contact Us
                        </a>
                      </div>
                    </Col>
                  </>
                )}
              </Row>
            </Col>
          </Row>
        </div>
      </footer>

      {/* Product Details Modal */}
      <Modal
        open={productModalVisible}
        onCancel={() => {
          setProductModalVisible(false);
          setSelectedProduct(null);
        }}
        footer={null}
        width={800}
        centered
        styles={{
          body: {
            padding: "0",
          },
        }}
      >
        {selectedProduct && (
          <div>
            {/* Modal Header with Icon */}
            <div
              style={{
                background: `linear-gradient(135deg, ${selectedProduct.color}15 0%, ${selectedProduct.color}25 100%)`,
                padding: "40px",
                borderRadius: "8px 8px 0 0",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Decorative circles */}
              <div
                style={{
                  position: "absolute",
                  top: "-50px",
                  right: "-50px",
                  width: "200px",
                  height: "200px",
                  borderRadius: "50%",
                  background: `${selectedProduct.color}20`,
                  filter: "blur(40px)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "-30px",
                  left: "-30px",
                  width: "150px",
                  height: "150px",
                  borderRadius: "50%",
                  background: `${selectedProduct.color}15`,
                  filter: "blur(30px)",
                }}
              />
              
              <div style={{ position: "relative", zIndex: 1 }}>
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "16px",
                    backgroundColor: `${selectedProduct.color}25`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "20px",
                    color: selectedProduct.color,
                    fontSize: "40px",
                  }}
                >
                  {selectedProduct.icon}
                </div>
                <Title
                  level={2}
                  style={{
                    marginBottom: "8px",
                    color: isDark ? "#ffffff" : "#000000",
                    fontSize: "32px",
                  }}
                >
                  {selectedProduct.title}
                </Title>
              </div>
            </div>

            {/* Modal Content */}
            <div style={{ padding: "40px" }}>
              {/* Description */}
              <Paragraph
                style={{
                  fontSize: "16px",
                  lineHeight: "1.8",
                  color: isDark ? "#e0e0e0" : "#333333",
                  marginBottom: "32px",
                }}
              >
                {selectedProduct.description}
              </Paragraph>

              {/* Key Features Section */}
              <div style={{ marginBottom: "32px" }}>
                <Title
                  level={4}
                  style={{
                    color: isDark ? "#ffffff" : "#000000",
                    marginBottom: "20px",
                    fontSize: "20px",
                  }}
                >
                  <CheckCircleOutlined style={{ color: selectedProduct.color, marginRight: "8px" }} />
                  Key Features
                </Title>
                <Row gutter={[16, 16]}>
                  {(isBankLingo || isPlatformClient) ? (
                    // Platform features
                    <>
                      {selectedProduct.id === "core_banking" && (
                        <>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Account Management</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Complete savings and current account handling</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Loan Origination</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>End-to-end loan lifecycle management</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Digital Wallet</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Optional wallet functionality for mobile money</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Multi-Currency</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Support for multiple currencies and exchange rates</Text>
                              </div>
                            </div>
                          </Col>
                        </>
                      )}
                      {selectedProduct.id === "reconciliation" && (
                        <>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Automated Matching</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Smart algorithms match transactions automatically</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Exception Handling</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Intelligent identification of discrepancies</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Multi-Channel</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Reconcile across all banking channels</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Detailed Reports</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Comprehensive reconciliation reports</Text>
                              </div>
                            </div>
                          </Col>
                        </>
                      )}
                      {selectedProduct.id === "transaction_monitoring" && (
                        <>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Real-Time Monitoring</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Live visibility into all transactions</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Fraud Detection</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>AI-powered anomaly detection</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Compliance Tools</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Built-in regulatory compliance checks</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Custom Dashboards</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Configurable monitoring dashboards</Text>
                              </div>
                            </div>
                          </Col>
                        </>
                      )}
                      {selectedProduct.id === "integration" && (
                        <>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>RESTful APIs</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Modern REST API architecture</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Pre-built Connectors</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Connect to payment gateways instantly</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Webhook Support</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Real-time event notifications</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>API Documentation</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Comprehensive developer docs</Text>
                              </div>
                            </div>
                          </Col>
                        </>
                      )}
                      {selectedProduct.id === "workflow" && (
                        <>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>BPMN 2.0 Standard</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Industry-standard workflow design</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Visual Designer</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Drag-and-drop workflow builder</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Approval Chains</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Multi-level approval workflows</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>High Performance</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Process thousands of transactions per second</Text>
                              </div>
                            </div>
                          </Col>
                        </>
                      )}
                      {selectedProduct.id === "loan_origination" && (
                        <>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Credit Scoring</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Automated creditworthiness assessment</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Document Management</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Digital document capture and verification</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Quick Approvals</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Fast-track loan approval process</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Disbursement</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Automated loan disbursement</Text>
                              </div>
                            </div>
                          </Col>
                        </>
                      )}
                    </>
                  ) : (
                    // Banking features
                    <>
                      {selectedProduct.id === "accounts" && (
                        <>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Savings Accounts</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Competitive interest rates</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Current Accounts</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Zero balance maintenance</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>24/7 Access</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Anytime, anywhere banking</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Account Statements</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Instant downloadable statements</Text>
                              </div>
                            </div>
                          </Col>
                        </>
                      )}
                      {selectedProduct.id === "transfers" && (
                        <>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Instant Transfers</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Real-time money movement</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Scheduled Payments</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Set up recurring transfers</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Beneficiary Management</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Save favorite recipients</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Transfer History</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Complete transaction records</Text>
                              </div>
                            </div>
                          </Col>
                        </>
                      )}
                      {selectedProduct.id === "loans" && (
                        <>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Multiple Loan Types</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Personal, business, and asset financing</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Quick Approval</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Fast loan processing</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Flexible Repayment</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Choose your repayment schedule</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Competitive Rates</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Affordable interest rates</Text>
                              </div>
                            </div>
                          </Col>
                        </>
                      )}
                      {selectedProduct.id === "bills" && (
                        <>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Multiple Billers</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Electricity, water, internet, and more</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Auto-Pay</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Set up automatic payments</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Payment Reminders</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Never miss a due date</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Payment History</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Track all bill payments</Text>
                              </div>
                            </div>
                          </Col>
                        </>
                      )}
                      {selectedProduct.id === "airtime_data" && (
                        <>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>All Networks</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Support for major mobile networks</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Instant Delivery</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Immediate airtime/data credit</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Data Bundles</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Various data package options</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Discounts</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Special offers for frequent users</Text>
                              </div>
                            </div>
                          </Col>
                        </>
                      )}
                      {selectedProduct.id === "savings_investment" && (
                        <>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Fixed Deposits</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>High-yield fixed term deposits</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Target Savings</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Save towards specific goals</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Investment Plans</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Diverse investment opportunities</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Growth Tracking</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Monitor your wealth growth</Text>
                              </div>
                            </div>
                          </Col>
                        </>
                      )}
                      {selectedProduct.id === "virtual_accounts" && (
                        <>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Multiple Accounts</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Create unlimited virtual accounts</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Instant Creation</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Generate accounts in seconds</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Fund Segregation</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Separate funds for different purposes</Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                              <CheckCircleOutlined style={{ color: selectedProduct.color, fontSize: "16px", marginTop: "4px" }} />
                              <div>
                                <Text strong style={{ color: isDark ? "#fff" : "#000", display: "block" }}>Auto Collections</Text>
                                <Text style={{ color: isDark ? "#999" : "#666", fontSize: "13px" }}>Automated payment collections</Text>
                              </div>
                            </div>
                          </Col>
                        </>
                      )}
                    </>
                  )}
                </Row>
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "12px", marginTop: "32px" }}>
                {isBankLingo && (
                  <Button
                    type="primary"
                    size="large"
                    icon={<ApiOutlined />}
                    onClick={() => window.open("https://documentation.banklingo.app", "_blank")}
                    style={{
                      backgroundColor: selectedProduct.color,
                      borderColor: selectedProduct.color,
                      height: "48px",
                      fontSize: "16px",
                      fontWeight: "600",
                      flex: 1,
                    }}
                  >
                    View Documentation
                  </Button>
                )}
                <Button
                  size="large"
                  onClick={() => {
                    setProductModalVisible(false);
                    setSelectedProduct(null);
                  }}
                  style={{
                    height: "48px",
                    fontSize: "16px",
                    borderColor: selectedProduct.color,
                    color: selectedProduct.color,
                    flex: isBankLingo ? 0 : 1,
                    minWidth: "120px",
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Loan Details Modal */}
      <Modal
        open={loanModalVisible}
        onCancel={() => {
          setLoanModalVisible(false);
          setSelectedLoan(null);
        }}
        footer={null}
        width={700}
        centered
        styles={{
          body: {
            padding: "32px",
          },
        }}
      >
        {selectedLoan && (
          <div>
            {/* Loan Image */}
            <div
              style={{
                marginBottom: "24px",
                borderRadius: "12px",
                overflow: "hidden",
                height: "300px",
              }}
            >
              <img
                src={selectedLoan.image}
                alt={selectedLoan.title}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>

            {/* Loan Title */}
            <Title
              level={2}
              style={{
                marginBottom: "16px",
                color: tenant.theme.colorPrimary,
                fontSize: "28px",
              }}
            >
              {selectedLoan.title}
            </Title>

            {/* Loan Description */}
            <Paragraph
              style={{
                fontSize: "16px",
                lineHeight: "1.8",
                color: isDark ? "#e0e0e0" : "#333333",
                marginBottom: "32px",
              }}
            >
              {selectedLoan.description}
            </Paragraph>

            {/* Action Buttons */}
            <div style={{ display: "flex", gap: "12px" }}>
              <Button
                type="primary"
                size="large"
                block
                onClick={() => {
                  setLoanModalVisible(false);
                  navigate(selectedLoan.url);
                }}
                style={{
                  backgroundColor: tenant.theme.colorPrimary,
                  borderColor: tenant.theme.colorPrimary,
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
              >
                Apply Now
              </Button>
              <Button
                size="large"
                onClick={() => {
                  setLoanModalVisible(false);
                  setSelectedLoan(null);
                }}
                style={{
                  height: "48px",
                  fontSize: "16px",
                  borderColor: tenant.theme.colorPrimary,
                  color: tenant.theme.colorPrimary,
                }}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LandingPage;
